import {
    Autocomplete,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from "@mui/material";
import useSearchLocations from "hooks/api/useSearchLocations";
import EditIcon from "@mui/icons-material/Edit";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { GlobalAppDataStorageService } from "infra/storage";
import SocialMediaURL from "services/SocialMediaURL.service";
import getErrorMessage from "assets/data/errors.json";
import envServiceInstance from "infra/envs/Env.service";
import { useSelector } from "react-redux";
import iso8601ToDate from "utils/iso8601ToDate";
import { CatButton } from "assets/styled-components/CatButton";

const validateLinkedinProfile = (value) => {
    if (!value) {
        return true;
    }

    if (!SocialMediaURL.isLinkedInDomain(value)) {
        return getErrorMessage.social.linkedin.invalid;
    }

    if (SocialMediaURL.isRecruiterAccount(value)) {
        return getErrorMessage.social.linkedin.recruiter;
    }

    if (!SocialMediaURL.containsLinkedinId(value)) {
        return getErrorMessage.social.linkedin.badUrl;
    }

    return true;
};

export default function CandidateInformationForm({ onSubmit }) {
    const currentCandidateAI = useSelector((state) => state.currentCandidateAI);
    const candidateInfo = currentCandidateAI?.candidateInformation;
    const {
        firstName,
        location,
        military,
        lastName,
        headline,
        email,
        phone,
        linkedin,
    } = candidateInfo || {};

    const defaultValues = useMemo(() => {
        const military = candidateInfo?.military;
        const linkedinValue = candidateInfo?.linkedin?.data;
        const defaultMilitaryStatus =
            military?.obligation === true
                ? 1
                : military?.obligation === false
                  ? 0
                  : "";

        return {
            firstName: candidateInfo?.firstName?.data ?? "",
            lastName: candidateInfo?.lastName?.data ?? "",
            headline: candidateInfo?.headline?.data ?? "",
            email: candidateInfo?.email?.data ?? "",
            dateOfBirth:
                iso8601ToDate(candidateInfo?.dateOfBirth?.data?.iso8601) ?? "",
            location: candidateInfo?.location?.data ?? null,
            phone: candidateInfo?.phone?.data ?? "",
            linkedin: linkedinValue
                ? SocialMediaURL.isLinkedInDomain(linkedinValue)
                    ? linkedinValue
                    : `${SocialMediaURL.LINKEDIN_PROFILE_BASE_URL}/${linkedinValue}`
                : "",
            linqId: candidateInfo?.linqId?.data
                ? `${SocialMediaURL.LINQ_PROFILE_BASE_URL}/${candidateInfo.linqId.data}`
                : "",
            anonymousReport: candidateInfo?.anonymousReport?.data ?? "",
            calling_code_id: candidateInfo?.phoneCode ?? null,
            military_obligations: defaultMilitaryStatus,
            defer_until: military?.deferment ?? "",
        };
    }, [candidateInfo]);
    const {
        handleSubmit,
        control,
        register,
        reset,
        clearErrors,
        setError,
        formState: { errors, isSubmitted },
    } = useForm({
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const [isLocationUpdated, setIsLocationUpdated] = useState(false);
    const {
        isSearchLocationsLoading,
        locations,
        fetchLocations,
        setLocations,
    } = useSearchLocations();

    const callingCodeOptions = GlobalAppDataStorageService.getCallingCodes();
    const phoneValidationMessages = {
        missingCallingCode: "Country code is required when phone is entered",
        missingPhone: "Phone is required when country code is selected",
    };

    const handleLocationInputChange = (_, value, reason) => {
        if (reason === "clear") {
            setLocations([]);
        }

        if (reason === "input") {
            fetchLocations(value);
        }
    };
    const shouldLocationBeDropdown = () => {
        return isLocationUpdated || !location?.data?.title;
    };

    const obligation = [
        { id: 0, name: "Fulfilled" },
        { id: 1, name: "Pending" },
        { id: "", name: "Not available" },
    ];

    const defaultMilitaryStatus = () => {
        if (military?.obligation === true) {
            return 1;
        } else if (military?.obligation === false) {
            return 0;
        } else {
            return "";
        }
    };

    const hasMilitaryObligations = useWatch({
        control,
        name: "military_obligations",
        defaultValue: defaultMilitaryStatus(),
    });
    const phoneField = register("phone");
    const phoneValue = useWatch({
        control,
        name: phoneField.name,
        defaultValue: defaultValues.phone,
    });
    const callingCodeValue = useWatch({
        control,
        name: "calling_code_id",
        defaultValue: defaultValues.calling_code_id,
    });
    const hasPhoneValue = Boolean(phoneValue?.trim());
    const hasCallingCodeValue = Boolean(
        [
            callingCodeValue?.id,
            callingCodeValue?.name,
            callingCodeValue?.calling_code,
        ].find(
            (value) =>
                value !== null &&
                value !== undefined &&
                `${value}`.trim() !== "",
        ),
    );
    const arePhoneFieldsValid =
        (!hasPhoneValue && !hasCallingCodeValue) ||
        (hasPhoneValue && hasCallingCodeValue);

    useEffect(() => {
        if (!isSubmitted) {
            return;
        }

        if (arePhoneFieldsValid) {
            clearErrors(["phone", "calling_code_id"]);
            return;
        }

        if (!hasPhoneValue) {
            setError("phone", {
                type: "manual",
                message: phoneValidationMessages.missingPhone,
            });
            clearErrors("calling_code_id");
            return;
        }

        setError("calling_code_id", {
            type: "manual",
            message: phoneValidationMessages.missingCallingCode,
        });
        clearErrors("phone");
    }, [
        arePhoneFieldsValid,
        clearErrors,
        hasPhoneValue,
        isSubmitted,
        phoneValidationMessages.missingCallingCode,
        phoneValidationMessages.missingPhone,
        setError,
    ]);

    const handleCandidateInformationSubmit = (data) => {
        if (!arePhoneFieldsValid) {
            if (!hasPhoneValue) {
                setError("phone", {
                    type: "manual",
                    message: phoneValidationMessages.missingPhone,
                });
                clearErrors("calling_code_id");
                return;
            }

            setError("calling_code_id", {
                type: "manual",
                message: phoneValidationMessages.missingCallingCode,
            });
            clearErrors("phone");
            return;
        }
        clearErrors(["phone", "calling_code_id"]);
        onSubmit(data);
    };

    return (
        <Box
            onSubmit={handleSubmit(handleCandidateInformationSubmit)}
            component="form"
            sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: "repeat(4, 1fr)",
            }}
        >
            <TextField
                label="First Name"
                name="firstName"
                required={firstName?.metadata?.required || false}
                {...register("firstName")}
                fullWidth
                variant="outlined"
                sx={{ gridColumn: { xs: "span 4", sm: "span 2" } }}
            />
            <TextField
                label="Last Name"
                name="lastName"
                required={lastName?.metadata?.required || false}
                {...register("lastName")}
                fullWidth
                variant="outlined"
                sx={{ gridColumn: { xs: "span 4", sm: "span 2" } }}
            />
            <TextField
                label="Headline"
                name="headline"
                required={headline?.metadata?.required || false}
                {...register("headline")}
                fullWidth
                variant="outlined"
                sx={{ gridColumn: "span 4" }}
            />
            <TextField
                {...register("dateOfBirth")}
                label="Date of Birth"
                InputLabelProps={{
                    shrink: true,
                }}
                name="dateOfBirth"
                sx={{ gridColumn: { xs: "span 4", sm: "span 2" } }}
                type="date"
            />
            <TextField
                label="Email"
                {...register("email")}
                name="email"
                required={email?.metadata?.required || false}
                variant="outlined"
                type="email"
                sx={{ gridColumn: { xs: "span 4", sm: "span 2" } }}
            />
            {!shouldLocationBeDropdown() && (
                <TextField
                    InputProps={{
                        endAdornment: (
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <EditIcon
                                    onClick={() => setIsLocationUpdated(true)}
                                    sx={{ cursor: "pointer" }}
                                />
                            </Box>
                        ),
                    }}
                    sx={{
                        gridColumn: "span 4",
                        "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#000", // override default grey
                            opacity: 1, // optional: reset opacity
                        },
                    }}
                    disabled
                    fullWidth
                    name="location"
                    defaultValue={location?.data?.title || undefined}
                    required={location?.metadata?.required || false}
                    error={Boolean(errors.position)}
                    helperText={errors.position ? errors.position.message : ""}
                    label="Location"
                />
            )}
            {shouldLocationBeDropdown() && (
                <Controller
                    name="location"
                    control={control}
                    defaultValue={""}
                    render={({ field }) => {
                        return (
                            <Autocomplete
                                filterOptions={(x) => x}
                                id="location"
                                fullWidth
                                autoComplete
                                {...register("location")}
                                loading={isSearchLocationsLoading}
                                noOptionsText="No locations found"
                                options={locations}
                                {...field}
                                sx={{
                                    gridColumn: "span 4",
                                }}
                                getOptionLabel={(option) =>
                                    typeof option === "string"
                                        ? option
                                        : (option?.title ?? "")
                                }
                                isOptionEqualToValue={(option, value) => {
                                    if (typeof option === "string") {
                                        return option === value;
                                    }
                                    return option?.id === value?.id;
                                }}
                                onInputChange={handleLocationInputChange}
                                onChange={(_, newValue) => {
                                    field.onChange(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        sx={{
                                            gridColumn: "span 4",
                                        }}
                                        label="Location"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                )}
                            />
                        );
                    }}
                />
            )}
            <Controller
                name="calling_code_id"
                control={control}
                render={({ field, fieldState }) => (
                    <Autocomplete
                        id="calling_code_id"
                        autoComplete
                        limitTags={1}
                        fullWidth
                        options={callingCodeOptions}
                        getOptionLabel={(option) => option?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                            option.name === value.name || {}
                        }
                        {...field}
                        onChange={(_, newValue) => {
                            field.onChange(newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Country code"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message || ""}
                            />
                        )}
                        sx={{
                            gridColumn: { xs: "span 4", sm: "span 2" },
                        }}
                    />
                )}
            />
            <TextField
                name={phoneField.name}
                inputRef={phoneField.ref}
                onBlur={phoneField.onBlur}
                onChange={phoneField.onChange}
                required={phone?.metadata?.required || false}
                error={Boolean(errors.phone)}
                helperText={errors.phone?.message || ""}
                label="Phone"
                sx={{
                    gridColumn: { xs: "span 4", sm: "span 2" },
                }}
            />
            <TextField
                sx={{
                    gridColumn: { xs: "span 4", sm: "span 2" },
                }}
                name="linkedin"
                {...register("linkedin", {
                    validate: validateLinkedinProfile,
                })}
                required={linkedin?.metadata?.required || false}
                error={Boolean(errors.linkedin)}
                helperText={errors.linkedin ? errors.linkedin.message : ""}
                label="Linkedin profile"
            />
            <TextField
                sx={{
                    gridColumn: { xs: "span 4", sm: "span 2" },
                }}
                name="linqId"
                {...register("linqId", {
                    validate: {
                        isLinqDomain: (value) => {
                            if (!value) return true;

                            return (
                                SocialMediaURL.isLinqDomain(value) ||
                                `${
                                    getErrorMessage.linq.invalid
                                }: ${envServiceInstance.getEmployerLinqBaseUrl()}`
                            );
                        },
                        containsStudent: (value) => {
                            if (!value) return true;

                            return (
                                SocialMediaURL.hasStudentParam(value) ||
                                getErrorMessage.linq.badUrl
                            );
                        },
                    },
                })}
                error={Boolean(errors.linqId)}
                helperText={errors.linqId ? errors.linqId.message : ""}
                label="Linq profile"
            />
            <Controller
                name="military_obligations"
                control={control}
                defaultValue={defaultMilitaryStatus()}
                render={({ field }) => (
                    <FormControl
                        sx={{ gridColumn: { xs: "span 4", sm: "span 2" } }}
                    >
                        <InputLabel id="military-obligation">
                            Military Obligation
                        </InputLabel>
                        <Select
                            {...field}
                            labelId="military-obligation"
                            label="Military Obligation"
                        >
                            {obligation.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            />
            <TextField
                InputLabelProps={{
                    shrink: true,
                }}
                disabled={!hasMilitaryObligations}
                name="defer_until"
                {...register("defer_until", {})}
                error={Boolean(errors.position)}
                helperText={errors.position ? errors.position.message : ""}
                label="Defferment of military service"
                type="date"
                sx={{ gridColumn: { xs: "span 4", sm: "span 2" } }}
            />
            <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                multiline
                minRows={5}
                name="anonymousReport"
                {...register("anonymousReport")}
                error={Boolean(errors.anonymousReport)}
                helperText={errors.anonymousReport?.message || ""}
                label="Access to Talent Anonymous Report"
                type="textarea"
                sx={{ gridColumn: "span 4" }}
            />
            <Stack
                direction="row"
                spacing={2}
                sx={{
                    mt: 2,
                    gridColumn: "span 4",
                    position: "sticky",
                    bottom: "0",
                    right: "0",
                    zIndex: 10,
                    justifyContent: "flex-end",
                    backgroundColor: "white",
                    margin: "0 -24px",
                    padding: "8px 16px",
                    boxShadow: "0 -2px 4px rgba(0,0,0,0.1)",
                    alignContent: "center",
                }}
            >
                <CatButton type="submit" size="medium" variant="contained">
                    Submit Candidate Information
                </CatButton>
            </Stack>
        </Box>
    );
}
